import getLoyaltyPointBurnCalculationKey from 'checkout/lib/utils/loyalty/getLoyaltyPointBurnCalculationKey'
import { EmptyArray, sortBy, sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import getAllItemsDiscountTotals from '../payment/getAllItemsDiscountTotals'
import getCreditPayAmount from '../payment/getCreditPayAmount'
import getPartnershipModifierAmount from '../payment/getPartnershipModifierAmount'
import getGiftCardPayAmount from '../payment/getGiftCardPayAmount'
import getAllItemViews from '../view/getAllItemViews'
import getCheckoutLoyaltyItemsPriceInCurrency from './getCheckoutLoyaltyItemsPriceInCurrency'
import getCheckoutLoyaltyPointsBurnCalculationRequests from './getCheckoutLoyaltyPointsBurnCalculationRequests'

const LOADING_VALUE: App.StatefulData<App.Checkout.LoyaltyPointBurnCalculationView> = {
  fetching: true,
}
const EMPTY_VALUE: App.StatefulData<App.Checkout.LoyaltyPointBurnCalculationView> = {
  fetching: false,
  data: {
    burnableItems: EmptyArray,
    displayedBurnableItems: EmptyArray,
    nonBurnableItems: EmptyArray,
    itemsTotalPriceInPoints: 0,
    maxBurnablePoints: 0,
    minBurnablePoints: 0,
  },
}

/**
 * View items are sorted by the highest point-to-currency ratio in desc order.
 */
const getCheckoutLoyaltyPointBurnCalculationView = createSelector(
  (state: App.State) => state.loyalty.account,
  (state: App.State) => getCheckoutLoyaltyPointsBurnCalculationRequests(state),
  (state: App.State) => state.loyalty.pointsBurnCalculations,
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getCheckoutLoyaltyItemsPriceInCurrency(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => getCreditPayAmount(state),
  (state: App.State) => getPartnershipModifierAmount(state),
  (state: App.State) => getGiftCardPayAmount(state),
  (
    loyaltyAccount,
    pointsBurnCalculationRequests,
    pointsBurnCalculations,
    allItemViews,
    itemsPriceInCurrency,
    allItemsDiscountTotals,
    creditPaymentAmount,
    partnershipModifierAmount,
    giftCardPayAmount,
  ): App.StatefulData<App.Checkout.LoyaltyPointBurnCalculationView> => {
    if (loyaltyAccount.initial || loyaltyAccount.fetching) return LOADING_VALUE
    if (!loyaltyAccount.data) return EMPTY_VALUE
    if (!allItemViews.hasRequiredData) return LOADING_VALUE
    if (!pointsBurnCalculationRequests.length) return EMPTY_VALUE

    const calculationKey = getLoyaltyPointBurnCalculationKey(pointsBurnCalculationRequests)
    const pointsBurnCalculation = pointsBurnCalculations[calculationKey]
    if (!pointsBurnCalculation?.data) return LOADING_VALUE

    let burnableItems: Array<App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem> = []
    const nonBurnableItems: Array<App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem> = []
    pointsBurnCalculation.data.items.forEach((calcItem) => {
      const itemView = allItemViews.data.get(calcItem.itemId)?.data
      if (itemView) {
        const actualPriceInCurrency = calcItem.priceInCurrency
        const actualPriceInPoints = calcItem.priceInPoints ?? 0
        let payablePriceInCurrency = actualPriceInCurrency
        let payablePriceInPoints = actualPriceInPoints
        const pricing = itemsPriceInCurrency.get(itemView.item.itemId)
        const loyaltyBenefitDiscount = pricing?.loyaltyUpgradeTargetPrice ? (pricing.loyaltyUpgradeTargetPrice - pricing.price) : 0
        const promoDiscount = allItemsDiscountTotals.get(itemView.item.itemId) ?? 0
        const itemDiscount = promoDiscount + loyaltyBenefitDiscount
        if (itemDiscount) {
          payablePriceInCurrency -= itemDiscount
          const burnRatio = actualPriceInPoints / actualPriceInCurrency
          payablePriceInPoints = Math.round(payablePriceInCurrency * burnRatio)
        }

        const item: App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem = {
          itemView,
          payablePriceInCurrency,
          actualPriceInCurrency,
          payablePriceInPoints,
          actualPriceInPoints,
          title: itemView.title,
          appliedCredits: 0,
          promoDiscount,
          loyaltyBenefitDiscount,
        }
        if (item.payablePriceInPoints && calcItem.isRedeemableWithPoints) burnableItems.push(item)
        if (!calcItem.isRedeemableWithPoints && item.actualPriceInCurrency > 0) nonBurnableItems.push(item)
      }
    }, [])

    const creditsApplied = creditPaymentAmount + partnershipModifierAmount + giftCardPayAmount

    if (creditsApplied) {
      // sort from worst to best burn ratio to start applying credits to the worst ones first
      const preCreditsBurnableItems = sortBy(
        burnableItems,
        (i) => i.actualPriceInPoints / i.actualPriceInCurrency,
        'desc',
      )
      const adjustedBurnableItems: Array<App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem> = []
      let creditsToApply = creditsApplied
      for (const preCreditBurnableItem of preCreditsBurnableItems) {
        if (creditsToApply <= 0) {
          adjustedBurnableItems.push(preCreditBurnableItem)
          continue
        }
        const creditsAppliedToItemPrice = Math.min(
          creditsToApply,
          preCreditBurnableItem.payablePriceInCurrency,
        )

        if (creditsAppliedToItemPrice !== preCreditBurnableItem.payablePriceInCurrency) {
          const adjustedPriceInCurrency = preCreditBurnableItem.payablePriceInCurrency - creditsAppliedToItemPrice
          const adjustPriceInPoints = Math.round((adjustedPriceInCurrency * preCreditBurnableItem.payablePriceInPoints) / preCreditBurnableItem.payablePriceInCurrency)
          adjustedBurnableItems.push({
            ...preCreditBurnableItem,
            payablePriceInCurrency: adjustedPriceInCurrency,
            payablePriceInPoints: adjustPriceInPoints,
            appliedCredits: creditsAppliedToItemPrice,
          })
        }
        creditsToApply -= creditsAppliedToItemPrice
      }
      burnableItems = adjustedBurnableItems
    }

    const itemsTotalPriceInPoints = sum(burnableItems, (item) => item.payablePriceInPoints)

    const maxBurnablePoints = Math.round(Math.min(
      loyaltyAccount.data.points,
      itemsTotalPriceInPoints,
      pointsBurnCalculation.data.maxBurnablePoints,
    ))

    // sort by point-to-currency ratio
    let displayedBurnableItems: Array<App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem> = sortBy(burnableItems, (item) => item.actualPriceInPoints / item.actualPriceInCurrency, 'asc')

    const shopShippingItems = burnableItems.filter((item) => item.itemView.loyalty.productType === 'shop_shipping')
    const hasShopShippingItems = !!shopShippingItems.length

    if (hasShopShippingItems) {
      const nonShopShippingItems = burnableItems.filter((item) => item.itemView.loyalty.productType !== 'shop_shipping')
      const sortedNonShopShippingItems = sortBy(nonShopShippingItems, (item) => item.actualPriceInPoints / item.actualPriceInCurrency, 'asc')

      const lastShopItemIdx = sortedNonShopShippingItems.findLastIndex((item) => {
        const itemLoyaltyProductType = item.itemView.loyalty.productType!
        return itemLoyaltyProductType.includes('shop_')
      })

      /** We display as a single burnable item at checkout but treat as separate items post purchase */
      const mergedShopShippingItems: App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem = {
        ...shopShippingItems[0],
        payablePriceInPoints: sum(shopShippingItems, (item) => item.payablePriceInPoints),
        payablePriceInCurrency: sum(shopShippingItems, (item) => item.payablePriceInCurrency),
      }

      /** As per product request, display shop shipping items after other shop merchandise items */
      sortedNonShopShippingItems.splice(lastShopItemIdx + 1, 0, mergedShopShippingItems)
      displayedBurnableItems = sortedNonShopShippingItems
    }

    return {
      fetching: false,
      data: {
        burnableItems: sortBy(
          burnableItems,
          // sort by point-to-currency ratio
          (i) => i.actualPriceInPoints / i.actualPriceInCurrency,
          'asc',
        ),
        displayedBurnableItems,
        nonBurnableItems,
        itemsTotalPriceInPoints,
        maxBurnablePoints,
        minBurnablePoints: pointsBurnCalculation.data.minBurnablePoints,
      },
    }
  },
)

export default getCheckoutLoyaltyPointBurnCalculationView
